import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { db, callEdgeFunction, isAbortError } from '../../lib/supabase'
import { formatDate } from '../../lib/dateUtils'
import { ErrorAlert } from '../../components/ui'
import { SALUTATIONS, PROFESSIONAL_ROLES } from '../../lib/constants'

const STATUS_STYLES = {
  pending_approval: 'bg-amber-100 text-amber-700',
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-700',
  rejected: 'bg-gray-200 text-gray-700',
}

export default function FacilitatorManagement() {
  const { t, i18n } = useTranslation('admin')
  const salutationLabels = i18n.t('salutations', { ns: 'common', returnObjects: true })
  const roleLabels = i18n.t('professionalRoles', { ns: 'common', returnObjects: true })
  const locale = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en'
  const [facilitators, setFacilitators] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)
  const [filter, setFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadFacilitators()
  }, [])

  const loadFacilitators = async () => {
    try {
      const data = await db.getFacilitators()
      setFacilitators(data)
      setError(null)
    } catch (err) {
      if (isAbortError(err)) return
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (facilitatorId, action, email) => {
    const message = action === 'delete'
      ? t('facilitatorManagement.confirmDelete', { email })
      : t('facilitatorManagement.confirmAction', { action })
    if (!confirm(message)) return

    setActionLoading(facilitatorId)
    try {
      await callEdgeFunction('approve-facilitator', { facilitator_id: facilitatorId, action })
      await loadFacilitators()
    } catch (err) {
      setError(err.message)
    } finally {
      setActionLoading(null)
    }
  }

  const filtered = facilitators.filter(f => {
    if (filter !== 'all' && f.status !== filter) return false
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim()
      const fields = [f.salutation, f.full_name, f.email, f.organization_name]
      if (!fields.some(v => v && v.toLowerCase().includes(query))) return false
    }
    return true
  })

  const pendingCount = facilitators.filter(f => f.status === 'pending_approval').length

  if (loading) {
    return <div className="text-center py-12 text-gray-500">{t('facilitatorManagement.loadingFacilitators')}</div>
  }

  return (
    <div className="animate-fadeIn">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-2xl font-medium text-gray-800">
              {t('facilitatorManagement.title')}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {(searchQuery || filter !== 'all')
                ? t('facilitatorManagement.filteredCount', { filtered: filtered.length, total: facilitators.length })
                : t('facilitatorManagement.facilitatorCount', { count: facilitators.length })
              }
              {pendingCount > 0 && (
                <span className="ml-2 text-amber-600 font-semibold">
                  {t('facilitatorManagement.pendingApproval', { count: pendingCount })}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            {['all', 'pending_approval', 'active', 'suspended', 'rejected'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
                  filter === f
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(`facilitatorManagement.statusLabels.${f}`)}
              </button>
            ))}
          </div>

          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('facilitatorManagement.searchPlaceholder')}
              className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <ErrorAlert message={error} onDismiss={() => setError(null)} />

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {filter === 'all'
            ? t('facilitatorManagement.noFacilitators')
            : t('facilitatorManagement.noFilteredFacilitators', { status: t(`facilitatorManagement.statusLabels.${filter}`).toLowerCase() })
          }
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorManagement.tableHeaders.salutation')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorManagement.tableHeaders.name')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorManagement.tableHeaders.email')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorManagement.tableHeaders.organisation')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorManagement.tableHeaders.roleTitle')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorManagement.tableHeaders.status')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorManagement.tableHeaders.registered')}</th>
                <th className="text-right px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorManagement.tableHeaders.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(f => (
                <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4 text-gray-600 text-sm">{f.salutation ? (Array.isArray(salutationLabels) ? salutationLabels[SALUTATIONS.indexOf(f.salutation)] : f.salutation) || f.salutation : ''}</td>
                  <td className="px-5 py-4 font-medium text-gray-800">{f.full_name}</td>
                  <td className="px-5 py-4 text-gray-600 text-sm">{f.email}</td>
                  <td className="px-5 py-4 text-gray-600 text-sm">{f.organization_name}</td>
                  <td className="px-5 py-4 text-gray-600 text-sm">
                    {f.professional_role === 'Others' ? f.professional_role_other : (Array.isArray(roleLabels) ? roleLabels[PROFESSIONAL_ROLES.indexOf(f.professional_role)] : f.professional_role) || f.professional_role}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[f.status]}`}>
                      {t(`facilitatorManagement.statusLabels.${f.status}`)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-gray-500 text-sm">
                    {formatDate(f.created_at, locale)}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {f.status === 'pending_approval' && (
                        <>
                          <button
                            onClick={() => handleAction(f.id, 'approve')}
                            disabled={actionLoading === f.id}
                            className="px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                          >
                            {t('facilitatorManagement.actions.approve')}
                          </button>
                          <button
                            onClick={() => handleAction(f.id, 'reject')}
                            disabled={actionLoading === f.id}
                            className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                          >
                            {t('facilitatorManagement.actions.reject')}
                          </button>
                        </>
                      )}
                      {f.status === 'active' && (
                        <button
                          onClick={() => handleAction(f.id, 'suspend')}
                          disabled={actionLoading === f.id}
                          className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                          {t('facilitatorManagement.actions.suspend')}
                        </button>
                      )}
                      {(f.status === 'suspended' || f.status === 'rejected') && (
                        <button
                          onClick={() => handleAction(f.id, 'reactivate')}
                          disabled={actionLoading === f.id}
                          className="px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                        >
                          {t('facilitatorManagement.actions.reactivate')}
                        </button>
                      )}
                      {(f.status === 'rejected' || f.status === 'suspended') && (
                        <button
                          onClick={() => handleAction(f.id, 'delete', f.email)}
                          disabled={actionLoading === f.id}
                          className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                          {t('facilitatorManagement.actions.delete')}
                        </button>
                      )}
                      <Link
                        to={`/admin/facilitators/${f.id}`}
                        className="px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                      >
                        {t('facilitatorManagement.actions.view')}
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
